import React, { useRef, useLayoutEffect, useState, useEffect } from "react";
import { Button, Table } from "react-bootstrap";

interface SplInstruction {
  SplInstructionName: string;
  SplInstructionData: string;
}

const getAdjustedPosition = (
  position: { top: number; left?: number; right?: number } | null,
  elementHeight: number
) => {
  if (!position) {
    return { top: "20%", left: "50%", transform: "translateX(-50%)" };
  }

  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const modalWidth = viewportWidth * 0.5;
  const top = position.top;
  let left =
    position.left ?? viewportWidth - (position.right ?? 0) - modalWidth;

  // Prevent modal from going off-screen
  if (left + modalWidth > viewportWidth) {
    left = viewportWidth - modalWidth - 10;
  }
  if (left < 10) {
    left = 10;
  }

  const spaceBelow = viewportHeight - top;
  const adjustedTop =
    spaceBelow < elementHeight ? top - elementHeight - 10 : top;

  return {
    top: adjustedTop,
    left,
    transform: "none",
  };
};

interface SplInstructionsModalProps {
  show: boolean;
  onClose: () => void;
  data: SplInstruction[];
  position?: { top: number; left?: number; right?: number } | null;
}

const SplInstructionsModal: React.FC<SplInstructionsModalProps> = ({
  show,
  onClose,
  data,
  position,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [adjustedPos, setAdjustedPos] = useState<any>(null);

  useLayoutEffect(() => {
    if (show && position && modalRef.current) {
      const elementHeight = modalRef.current.offsetHeight || 300;
      setAdjustedPos(getAdjustedPosition(position, elementHeight));
    }
  }, [show, position]);

  // ✅ Close modal on outside click
  useEffect(() => {
    if (!show) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [show, onClose]);

  useEffect(() => {
    if (!show) return;

    const handleScroll = (e: Event) => {
      // If modal exists and the scroll target is inside modal, ignore
      if (modalRef.current && modalRef.current.contains(e.target as Node)) {
        return;
      }

      // ✅ Close modal on outside scroll
      onClose();
    };

    window.addEventListener("scroll", handleScroll, true); // use capture phase
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div
      ref={modalRef}
      className="loc-instruction"
      style={{
        position: "fixed",
        width: "50%",
        maxHeight: "45vh",
        background: "#fff",
        border: "1px solid #ccc",
        borderRadius: "8px",
        boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
        overflow: "hidden",
        zIndex: 99,
        ...(adjustedPos ?? {
          top: "20%",
          left: "50%",
          transform: "translateX(-50%)",
        }),
      }}
    >
      {/* <div className='arrow'></div> */}

      {/* Header with close button */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "2px 12px",
          fontSize: "15px",
        }}
      >
        <span className="btn-text fw-medium">Instructions</span>
        <Button
          variant="light"
          size="sm"
          onClick={onClose}
          style={{
            border: "none",
            background: "transparent",
            fontSize: "16px",
          }}
          className="btn-text fw-bold px-0"
        >
          <span className="material-symbols-outlined">close</span>
        </Button>
      </div>

      {/* Table content */}
      <div
        style={{
          maxHeight: "35vh",
          overflowY: "auto",
          padding: "0px 10px 10px",
        }}
      >
        <Table size="sm" className="instruction table mb-3">
          <thead>
            <tr>
              <th style={{ width: "10%", textAlign: "center" }}>Sr.No</th>
              <th style={{ width: "35%" }}>Category</th>
              <th style={{ width: "55%" }}>Description</th>
            </tr>
          </thead>
          <tbody>
            {data?.map((item, index) => (
              <tr key={index}>
                <td style={{ textAlign: "center" }}>
                  {String(index + 1).padStart(2, "0")}
                </td>
                <td>{item.SplInstructionName}</td>
                <td style={{ whiteSpace: "pre-line", textAlign: "left" }}>
                  {item.SplInstructionData || "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default SplInstructionsModal;
